"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { LineChart } from "@/src/components/charts/line-chart";

const basicData = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  series: [
    { name: "Revenue", data: [320, 380, 420, 510, 580, 680] },
    { name: "Expenses", data: [220, 280, 320, 400, 450, 520] },
    { name: "Net Income", data: [100, 100, 100, 110, 130, 160] },
  ],
};

const smoothData = {
  categories: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6", "Week 7", "Week 8"],
  series: [
    { name: "Orders", data: [120, 150, 180, 210, 240, 280, 320, 380] },
  ],
};

const dashedData = {
  categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  series: [
    { name: "Actual", data: [280, 320, 340, 380, 420, 460, 500] },
    { name: "Target", data: [300, 320, 360, 380, 400, 420, 450], dashed: true },
  ],
};

export default function DocsLineChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Line Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Show trends over time with connected data points. Use smooth curves
              for continuous data or dashed lines to distinguish actual vs. target values.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Multi-Series Line Chart</CardTitle>}
            contentClassName="p-theme-5"
          >
            <LineChart data={basicData} height={350} showSymbol={true} />
          </SectionCard>
        </Section>

        {/* Variants */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Smooth Curve</CardTitle>}
              contentClassName="p-theme-5"
            >
              <LineChart data={smoothData} height={300} smooth={true} showSymbol={true} />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Actual vs Target</CardTitle>}
              contentClassName="p-theme-5"
            >
              <LineChart data={dashedData} height={300} smooth={true} />
            </SectionCard>
          </div>
        </Section>

        {/* With Area Fill */}
        <Section title="With Area Fill">
          <SectionCard
            header={<CardTitle>Line Chart with Area Gradient</CardTitle>}
            contentClassName="p-theme-5"
          >
            <LineChart
              data={basicData}
              height={350}
              showArea={true}
              showSymbol={false}
            />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>LineChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">LineChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Categories and series data</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">100%</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">smooth</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Use smooth curves instead of straight lines</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showArea</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Fill area under lines with gradient</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show legend</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showSymbol</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show data point symbols (circles)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(value: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in tooltip</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onPointClick</td>
                  <td className="py-2 px-2">(params) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for points</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>LineChartData Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface LineChartData {
  categories: string[];
  series: {
    name: string;           // Series name in legend
    data: number[];         // Data points matching categories
    color?: string;         // Optional custom color (hex/CSS)
    dashed?: boolean;       // Render as dashed line
    showArea?: boolean;     // Fill area under this line
  }[];
}

// Example:
const data = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May"],
  series: [
    {
      name: "Actual",
      data: [280, 320, 340, 380, 420]
    },
    {
      name: "Target",
      data: [300, 320, 360, 380, 400],
      dashed: true
    }
  ]
};`}</code>
            </pre>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsLineChartPage as LineChartPage };
